import { RefObject, useEffect, useRef, useState } from 'react'

const SCROLL_EDGE_THRESHOLD = 1
const MOUNT_RETRY_DELAYS_MS = [0, 50, 150, 300]

export type UseScrollStatusResult = {
  isScrollable: boolean
  isScrolledToTop: boolean
  isScrolledToBottom: boolean
}

const DEFAULT_STATE: UseScrollStatusResult = {
  isScrollable: false,
  isScrolledToTop: true,
  isScrolledToBottom: true
}

export function useScrollStatus(targetRef: RefObject<HTMLElement | null>, deps: unknown[] = []): UseScrollStatusResult {
  const [state, setState] = useState<UseScrollStatusResult>(DEFAULT_STATE)

  const retryCountRef = useRef(0)
  const [mountRetry, setMountRetry] = useState(0)
  const prevDepsRef = useRef(deps)

  useEffect(() => {
    const depsChanged =
      prevDepsRef.current !== deps &&
      (prevDepsRef.current.length !== deps.length || prevDepsRef.current.some((d, i) => d !== deps[i]))

    if (depsChanged) {
      prevDepsRef.current = deps
      retryCountRef.current = 0
    }

    const el = targetRef.current

    if (!el) {
      setState(DEFAULT_STATE)

      const nextRetryIndex = retryCountRef.current + 1

      if (nextRetryIndex < MOUNT_RETRY_DELAYS_MS.length) {
        const delay = MOUNT_RETRY_DELAYS_MS[nextRetryIndex]

        const timeout = setTimeout(() => {
          retryCountRef.current = nextRetryIndex
          setMountRetry(r => r + 1)
        }, delay)

        return () => clearTimeout(timeout)
      }

      return undefined
    }

    retryCountRef.current = 0

    const checkScrollState = () => {
      const { scrollTop, scrollHeight, clientHeight } = el

      const isScrollable = scrollHeight > clientHeight
      const isScrolledToTop = scrollTop <= SCROLL_EDGE_THRESHOLD
      const isScrolledToBottom = Math.abs(scrollTop + clientHeight - scrollHeight) <= SCROLL_EDGE_THRESHOLD

      setState({ isScrollable, isScrolledToTop, isScrolledToBottom })
    }

    const runAfterLayout = (fn: () => void) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(fn)
      })
    }

    setState({
      isScrollable: el.scrollHeight > el.clientHeight,
      isScrolledToTop: true,
      isScrolledToBottom: false
    })

    runAfterLayout(checkScrollState)

    el.addEventListener('scroll', checkScrollState, { passive: true })

    const resizeObserver = new ResizeObserver(checkScrollState)
    resizeObserver.observe(el)

    return () => {
      el.removeEventListener('scroll', checkScrollState)
      resizeObserver.disconnect()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRef, ...deps, mountRetry])

  return state
}
